import { DarkMode, WbSunny } from "@mui/icons-material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarToday";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import EditIcon from "@mui/icons-material/Edit";
import type { SxProps, Theme } from "@mui/material";
import { Box, Button, FormControlLabel, IconButton, Switch, styled, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import type React from "react";
import { type Dispatch, type SetStateAction, useCallback, useRef, useState } from "react";
import { EditTimePopover } from "../components/EditTimePopover";
import { SystemDatePickerPopup } from "../components/UIToolkit/Popups/SystemDatePickerPopup";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { ALPHA, INK, INK_BLACK, NEUTRAL, SHIFT_DAY, SHIFT_NIGHT_FALLBACK, SURFACE } from "../theme/tokens";
import type { NurseRole } from "./models";

export const StyledHeader = styled(Box)({
	display: "flex",
	alignItems: "center",
	padding: "24px",
});

export const StyledTitle = styled(Box)({
	display: "flex",
	alignItems: "center",
	gap: "12px",
});

export const StyledTitleText = styled(Typography)(({ theme }) => ({
	fontSize: "20px",
	color: theme.palette.primary.main,
	fontWeight: 600,
}));

export const StyledText = styled(Typography)(({ theme }) => ({
	fontSize: "18px",
	color: theme.palette.primary.main,
	fontWeight: 400,
	margin: "10px 1px 5px 24px",
}));

export const StyledDateText = styled(Typography)(({ theme }) => ({
	color: theme.palette.primary.main,
	fontSize: "12px",
	fontWeight: 400,
	display: "flex",
	alignItems: "center",
	gap: "4px",
	"& .MuiSvgIcon-root": {
		fontSize: "16px",
	},
}));

export const StyledShiftBadge = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	gap: "8px",
	padding: "8px 16px",
	borderRadius: "100px",
	backgroundColor: SURFACE,
	border: `1px solid ${NEUTRAL[200]}`,
	marginLeft: "auto",
	color: INK.SECONDARY,
	fontSize: "14px",
	boxShadow: `0px 1px 2px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT / 2)}`,
	"& .MuiSvgIcon-root": {
		fontSize: "20px",
	},
	"& .night-icon": {
		color: theme.palette.primary.main,
	},
	"& .day-icon": {
		color: SHIFT_DAY,
	},
}));

export const StyledSwitchLabel = styled(FormControlLabel)({
	marginLeft: 0,
	width: "150px",
	"& .MuiFormControlLabel-label": {
		fontSize: "14px",
		color: INK.SECONDARY,
	},
	"& .MuiSwitch-root": {
		marginRight: "8px",
	},
});

export const StyledTextField = styled(TextField)({
	"& .MuiInputBase-input": {
		padding: "8px 12px",
		fontSize: "14px",
	},
});

export const StyledTableRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== "selected" })<{ selected: boolean }>(({ selected, theme }) => ({
	cursor: "pointer",
	backgroundColor: selected ? NEUTRAL[150] : "transparent",
	transition: "background-color 0.2s ease",
	borderLeft: selected ? `4px solid ${theme.palette.primary.main}` : "4px solid transparent",
	"&:hover": {
		backgroundColor: selected ? NEUTRAL[150] : NEUTRAL[50],
	},
	"& td": {
		padding: "8px 16px",
		borderBottom: `1px solid ${NEUTRAL[300]}`,
		borderRight: "none",
		fontSize: "14px",
		color: selected ? INK.PRIMARY : INK.SECONDARY,
		fontWeight: selected ? 500 : 400,
		"&:first-of-type": {
			paddingLeft: "24px",
		},
		"&:last-of-type": {
			paddingRight: "24px",
		},
	},
}));

export const StyledTableCell = styled(TableCell)({
	fontWeight: 500,
	padding: "4px 16px",
	backgroundColor: NEUTRAL[150],
	fontSize: "14px",
	borderLeft: "none",
	color: INK.SECONDARY,
	borderRight: `3px solid ${SURFACE}`,
	"&:first-of-type": {
		paddingLeft: "24px",
	},
	"&:last-of-type": {
		paddingRight: "24px",
		borderRight: "none",
	},
});

export const SortButton = styled(Box)({
	display: "inline-flex",
	alignItems: "center",
	cursor: "pointer",
	userSelect: "none",
	width: "100%",
	justifyContent: "inherit",
	gap: "4px",
	padding: "4px 0",
	color: INK.SECONDARY,
	fontWeight: 600,
	"&:hover": {
		opacity: 0.8,
	},
	"& .MuiSvgIcon-root": {
		fontSize: "24px",
	},
	"& .sort-icons": {
		display: "flex",
		flexDirection: "column",
		marginLeft: "4px",
		height: "28px",
	},
});

interface UpdateExistingScheduleToggleProps {
	selectedDate: DateTime | null;
	setSelectedDate: Dispatch<SetStateAction<DateTime | null>>;
	onToggleChange?: Dispatch<SetStateAction<boolean>>;
}

export const UpdateExistingScheduleToggle = ({ selectedDate, setSelectedDate, onToggleChange }: UpdateExistingScheduleToggleProps) => {
	const [updateExisting, setUpdateExisting] = useState(true);
	const minDate = DateTime.now();
	return (
		<>
			<StyledSwitchLabel
				control={
					<Switch
						checked={updateExisting}
						size="small"
						onChange={(e) => {
							setUpdateExisting(e.target.checked);
							setSelectedDate(e.target.checked ? DateTime.now() : null);
							onToggleChange?.(e.target.checked);
						}}
					/>
				}
				label="Update existing schedule"
				sx={{
					height: "56px",
					display: "flex",
					alignItems: "center",
					margin: 0,
					marginLeft: 2,
				}}
			/>
			{updateExisting && (
				<LocalizationProvider dateAdapter={AdapterLuxon}>
					<DatePicker
						value={selectedDate}
						onChange={(newValue: DateTime | null) => {
							if (newValue?.isValid && newValue.startOf("day") >= minDate.startOf("day")) {
								setSelectedDate(newValue);
							} else {
								setSelectedDate(null);
							}
						}}
						minDate={minDate}
						sx={{
							width: "150px",
							"& .MuiInputBase-root": {
								height: "56px",
							},
							"& .MuiInputBase-input": {
								fontSize: "14px",
								padding: "12px 14px",
							},
						}}
					/>
				</LocalizationProvider>
			)}
		</>
	);
};

interface NursePanelHeaderProps {
	shiftNo: number;
	setShiftNo?: Dispatch<SetStateAction<number>>;
	weekIndex: number;
	dayNo?: number;
	weeekday?: string;
	isMasterSchedule: boolean;
	title: string;
	shiftStart?: DateTime;
	shiftEnd?: DateTime;
	timezone?: string;
	setShiftStart?: Function | undefined;
	setShiftEnd?: Function | undefined;
	allowEditShiftTimes?: boolean;
	changeDate?: boolean;
	onSelectedDateChanged?: (date: DateTime) => void;
	role?: `${NurseRole}` | "default";
	/** Job the shift will land on; drives shift-length options in job-managed departments (DAT-580). */
	jobId?: string;
	onMessageIconClick?: () => void;
	allowPastDateSelection?: boolean;
	/**
	 * With `changeDate`: the earliest pickable date (e.g. the first loaded day). Takes precedence over
	 * `allowPastDateSelection`, which only knows "any" or "from today".
	 */
	minSelectableDate?: DateTime;
	/** With `changeDate`: the date button's accessible name ("Change date, Sat, Sep 26"). Already translated. */
	dateAriaLabel?: string;
	/** Test id of the date text / date button. */
	dateDataTestId?: string;
	titleCss?: SxProps<Theme>;
}

/** The earliest date the header's picker offers: an explicit bound, else "from today" unless past dates are allowed. */
const resolveMinPickerDate = (minSelectableDate: DateTime | undefined, allowPastDateSelection: boolean | undefined): DateTime | undefined => {
	if (minSelectableDate) return minSelectableDate;
	return allowPastDateSelection ? undefined : DateTime.now();
};

export const NursePanelHeader = ({
	shiftNo,
	setShiftNo,
	weekIndex,
	dayNo,
	isMasterSchedule,
	weeekday,
	title,
	timezone,
	shiftStart,
	shiftEnd,
	setShiftStart,
	setShiftEnd,
	allowEditShiftTimes,
	changeDate,
	onSelectedDateChanged,
	role,
	jobId,
	onMessageIconClick,
	allowPastDateSelection,
	minSelectableDate,
	dateAriaLabel,
	dateDataTestId,
	titleCss,
}: NursePanelHeaderProps) => {
	const { hospitalData, chosenDepartment, latestDateInWeeksData, isHospitalView } = useHospitalData();
	const [isEditingTime, setIsEditingTime] = useState<boolean>(false);
	const [anchorRef, setAnchorRef] = useState<HTMLElement>();
	const getCurrentDate = useCallback(() => {
		if (shiftStart) {
			return shiftStart.startOf("day");
		}
		if (dayNo !== undefined && hospitalData.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo]) {
			return hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].date;
		}
		return DateTime.now();
	}, [shiftStart, hospitalData, weekIndex, shiftNo, dayNo]);
	const [date, setDate] = useState<DateTime>(getCurrentDate);
	const getFormattedDate = useCallback(() => {
		return date.toFormat("ccc, MMM d");
	}, [date]);
	const dateTextRef = useRef<HTMLElement | null>(null);
	const [pickerOpen, setPickerOpen] = useState<boolean>(false);
	const togglePicker = useCallback(() => {
		setPickerOpen((prev) => !prev);
	}, []);
	const handleDateChange = useCallback(
		(value: DateTime | null) => {
			if (value?.isValid) {
				setDate(value);
				if (onSelectedDateChanged) {
					onSelectedDateChanged(value);
				}
			}
		},
		[onSelectedDateChanged],
	);
	const handleOnKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				togglePicker();
			}
		},
		[togglePicker],
	);

	return (
		<StyledHeader sx={titleCss}>
			{changeDate && (
				<SystemDatePickerPopup
					open={pickerOpen}
					onClose={togglePicker}
					anchorEl={dateTextRef.current}
					value={date}
					format={"MM/DD/yyyy"}
					onChange={handleDateChange}
					overridePickerProps={{
						maxDate: latestDateInWeeksData,
						minDate: resolveMinPickerDate(minSelectableDate, allowPastDateSelection),
					}}
				/>
			)}
			<StyledTitle>
				<StyledTitleText>{title}</StyledTitleText>
				<StyledDateText
					ref={dateTextRef}
					onClick={changeDate ? togglePicker : undefined}
					sx={
						changeDate
							? (theme) => ({
									cursor: "pointer",
									border: `1px solid ${theme.palette.primary.main}`,
									borderRadius: "4px",
									p: 0.25,
								})
							: undefined
					}
					tabIndex={0}
					onKeyDown={handleOnKeyDown}
					role={changeDate ? "button" : undefined}
					aria-haspopup={changeDate ? "dialog" : undefined}
					aria-label={changeDate ? dateAriaLabel : undefined}
					data-testid={dateDataTestId}
				>
					{!isMasterSchedule ? (
						<>
							<CalendarTodayOutlinedIcon sx={(theme) => ({ color: theme.palette.primary.main })} />
							{getFormattedDate()}
						</>
					) : (
						<>
							{weeekday?.substring(0, 3) ?? date.toFormat("ccc")},{`Week ${weekIndex + 1}/${chosenDepartment.master_schedule_weeks}`}
						</>
					)}
				</StyledDateText>
			</StyledTitle>
			<Box
				sx={{
					alignItems: "center",
					gap: 5,
					marginLeft: "auto",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{onMessageIconClick && (
						<IconButton size={"small"} onClick={onMessageIconClick}>
							<ChatOutlinedIcon fontSize={"small"} />
						</IconButton>
					)}
					<Box
						sx={{
							display: setShiftNo ? "flex" : "none",
							alignItems: "center",
							gap: 1,
							backgroundColor: NEUTRAL[150],
							borderRadius: "20px",
							padding: "4px",
							marginRight: "10px",
						}}
					>
						<Button
							onClick={() => !isMasterSchedule && setShiftNo?.(0)}
							sx={(theme) => ({
								cursor: isMasterSchedule ? "default" : "pointer",
								minWidth: "unset",
								padding: "4px 12px",
								borderRadius: "16px",
								textTransform: "none",
								backgroundColor: shiftNo === 0 ? SURFACE : "transparent",
								color: shiftNo === 0 ? INK.SECONDARY : theme.palette.primary.main,
								boxShadow: shiftNo === 0 ? `0px 1px 3px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT)}` : "none",
								"&:hover": {
									backgroundColor: shiftNo === 0 ? SURFACE : NEUTRAL[200],
								},
							})}
						>
							{/* Hospital demo shows the day/night icons in dark ink like the rest of the page. */}
							<WbSunny sx={{ fontSize: "16px", marginRight: "4px", color: isHospitalView ? INK_BLACK : SHIFT_DAY }} />
							Day
						</Button>
						<Button
							onClick={() => !isMasterSchedule && setShiftNo?.(1)}
							sx={(theme) => ({
								cursor: isMasterSchedule ? "default" : "pointer",
								minWidth: "unset",
								padding: "4px 12px",
								borderRadius: "16px",
								textTransform: "none",
								backgroundColor: shiftNo === 1 ? SURFACE : "transparent",
								color: shiftNo === 1 ? INK.SECONDARY : theme.palette.primary.main,
								boxShadow: shiftNo === 1 ? `0px 1px 3px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT)}` : "none",
								"&:hover": {
									backgroundColor: shiftNo === 1 ? SURFACE : NEUTRAL[200],
								},
							})}
						>
							<DarkMode sx={{ fontSize: "16px", marginRight: "4px", color: isHospitalView ? INK_BLACK : SHIFT_NIGHT_FALLBACK }} />
							Night
						</Button>
					</Box>
				</Box>
				{shiftStart && shiftEnd && (
					<Box sx={{ display: "flex" }}>
						<Typography sx={{ color: alpha(INK.SECONDARY, 0.5) }}>
							{shiftStart.setZone(timezone).toFormat("h:mm a")} - {shiftEnd.setZone(timezone).toFormat("h:mm a")}
						</Typography>
						{allowEditShiftTimes && (
							<IconButton
								className="edit-icon"
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									setAnchorRef(e.currentTarget);
									setIsEditingTime(true);
								}}
							>
								<EditIcon sx={{ fontSize: "1rem" }} />
							</IconButton>
						)}
						{isEditingTime && setShiftStart && setShiftEnd && (
							<EditTimePopover
								role={role}
								jobId={jobId}
								startTime={shiftStart}
								endTime={shiftEnd}
								onTimeChange={({ newStartTime, newEndTime }) => {
									setShiftStart(newStartTime);
									setShiftEnd(newEndTime);
								}}
								onClose={() => setIsEditingTime(false)}
								anchorEl={anchorRef!}
								isActual={false}
							/>
						)}
					</Box>
				)}
			</Box>
		</StyledHeader>
	);
};
